import { createFileRoute, Link } from "@tanstack/react-router";
import { PageImageGallery } from "@/components/page-image-gallery";
import { Clock, Mail, MapPin, Send } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ogImageMeta } from "@/lib/og";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact met de Redactie | Casino Betalingen" },
      {
        name: "description",
        content:
          "Fout gezien in een test of een vraag over een betaalmethode? Mail de redactie of bezoek ons in Amsterdam. Elke melding wordt door een redacteur gelezen.",
      },
      { property: "og:title", content: "Contact met de Redactie | Casino Betalingen" },
      {
        property: "og:description",
        content:
          "Fout gezien in een test of een vraag over een betaalmethode? Mail de redactie of bezoek ons in Amsterdam. Elke melding wordt door een redacteur gelezen.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
      ...ogImageMeta("contact"),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/contact/` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Beste Casino Betaalhulp & Contact | Casino Betalingen",
          url: `${SITE.url}/contact/`,
          mainEntity: {
            "@type": "Organization",
            name: SITE.name,
            url: `${SITE.url}/`,
            email: "redactie@casinobetalingen.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: SITE.address.street,
              postalCode: SITE.address.postalCode,
              addressLocality: SITE.address.city,
              addressCountry: "NL",
            },
          },
        }),
      },
    ],
  }),
  component: ContactPage,
});

// Herengracht 450, Amsterdam
const GEO = { lat: 52.3668, lon: 4.8874 };

function ContactPage() {
  const [verzonden, setVerzonden] = useState(false);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const naam = String(data.get("naam") ?? "");
    const onderwerp = String(data.get("onderwerp") ?? "");
    const bericht = String(data.get("bericht") ?? "");
    const email = String(data.get("email") ?? "");

    const body = encodeURIComponent(
      `Naam: ${naam}\nE-mail: ${email}\n\n${bericht}`,
    );
    window.location.href = `mailto:redactie@casinobetalingen.com?subject=${encodeURIComponent(
      `[Contact] ${onderwerp}`,
    )}&body=${body}`;
    setVerzonden(true);
  };

  return (
    <main className="container-site py-12">
      <nav aria-label="Breadcrumb" className="mb-6 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>{" "}
        / <span className="text-foreground">Contact</span>
      </nav>

      <div className="max-w-2xl">
        <h1 className="text-3xl font-bold sm:text-4xl">Contact</h1>
        <p className="mt-3 text-muted-foreground">
          Vragen over een betaalmethode, een correctie op een artikel of een
          mogelijke samenwerking? Stuur ons een bericht — de redactie reageert
          doorgaans binnen 2 werkdagen. Deze informatie is gecontroleerd op 9 oktober 2026.
        </p>
      </div>

      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_1.1fr]">
        {/* Bedrijfsgegevens + kaart */}
        <div className="space-y-6">
          <section className="card-surface p-6" aria-label="Bedrijfsgegevens">
            <h2 className="text-lg font-semibold">Bedrijfsgegevens</h2>
            <ul className="mt-4 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />
                <address className="not-italic leading-relaxed text-muted-foreground">
                  <strong className="block text-foreground">{SITE.name}</strong>
                  {SITE.address.street}
                  <br />
                  {SITE.address.postalCode} {SITE.address.city}
                  <br />
                  {SITE.address.country}
                </address>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />
                <div>
                  <span className="block text-foreground">E-mail</span>
                  <a
                    href="mailto:redactie@casinobetalingen.com"
                    className="text-muted-foreground underline-offset-2 hover:underline"
                  >
                    redactie@casinobetalingen.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />
                <div>
                  <span className="block text-foreground">Reactietijd</span>
                  <span className="text-muted-foreground">
                    Maandag t/m vrijdag, reactie binnen 2 werkdagen
                  </span>
                </div>
              </li>
            </ul>
          </section>

          <section className="card-surface overflow-hidden" aria-label="Kaart">
            <iframe
              title={`Kaart: ${SITE.address.street}, ${SITE.address.city}`}
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${GEO.lon - 0.006}%2C${GEO.lat - 0.003}%2C${GEO.lon + 0.006}%2C${GEO.lat + 0.003}&layer=mapnik&marker=${GEO.lat}%2C${GEO.lon}`}
              className="h-64 w-full border-0"
              loading="lazy"
            />
            <a
              href={`https://www.openstreetmap.org/?mlat=${GEO.lat}&mlon=${GEO.lon}#map=17/${GEO.lat}/${GEO.lon}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block px-4 py-3 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              Bekijk grotere kaart →
            </a>
          </section>
        </div>

        {/* Contactformulier */}
        <section className="card-surface p-6 sm:p-8" aria-label="Contactformulier">
          <h2 className="text-lg font-semibold">Stuur een bericht</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Velden met een * zijn verplicht. Je bericht opent in je eigen
            e-mailprogramma — wij slaan niets op.
          </p>
          <form onSubmit={onSubmit} className="mt-6 space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="naam">Naam *</Label>
                <Input id="naam" name="naam" required autoComplete="name" placeholder="Je naam" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-mailadres *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="jij@voorbeeld.nl"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="onderwerp">Onderwerp *</Label>
              <Input
                id="onderwerp"
                name="onderwerp"
                required
                placeholder="Bijv. correctie artikel iDEAL"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bericht">Bericht *</Label>
              <Textarea
                id="bericht"
                name="bericht"
                required
                rows={6}
                placeholder="Waar kunnen we je mee helpen?"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-success text-success-foreground hover:bg-success/90 sm:w-auto"
            >
              <Send className="mr-2 h-4 w-4" aria-hidden />
              {verzonden ? "Opnieuw versturen" : "Verstuur bericht"}
            </Button>
            <p className="text-xs text-muted-foreground">
              Door te versturen ga je akkoord met ons{" "}
              <Link to="/privacybeleid/" className="underline underline-offset-2">
                privacybeleid
              </Link>
              .
            </p>
          </form>
        </section>
      </div>
          <PageImageGallery path="/contact" title="Redactie Casino Betalingen" />
    </main>
  );
}
